import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AiCommandDto, AiService } from '../ai/ai.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';

const SECONDARY_STATUS_CLASS = 'bg-secondary';

@Component({
  selector: 'app-ai-command-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ai-command-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ai-command-list.component.scss'
})
export class AiCommandListComponent {
  @Input() commands: AiCommandDto[] = [];
  @Output() onDeleted = new EventEmitter<void>();
  @Output() onRetried = new EventEmitter<void>();

  expandedId: number | null = null;

  private readonly gitlabStatusColors: Record<string, string> = {
    'opened': 'bg-success',
  };

  private readonly strapiStatusColors: Record<string, string> = {
    'completelyOpen': 'bg-success',
  };

  constructor(
    private aiService: AiService,
    private toastr: ToastrService
  ) {}

  getGitlabStatusClass(status: string | null): string {
    if (!status) {
      return SECONDARY_STATUS_CLASS;
    }
    return this.gitlabStatusColors[status] || SECONDARY_STATUS_CLASS;
  }

  getStrapiStatusClass(status: string | null): string {
    if (!status) {
      return SECONDARY_STATUS_CLASS;
    }
    return this.strapiStatusColors[status] || SECONDARY_STATUS_CLASS;
  }

  toggleExpand(id: number): void {
    this.expandedId = this.expandedId === id ? null : id;
  }

  retryCommand(cmd: AiCommandDto, event: MouseEvent): void {
    event.stopPropagation();
    if (!cmd.command) {
      this.toastr.error('Cannot retry: command has no stored input');
      return;
    }
    this.aiService.retryAiCommand(cmd.command).subscribe({
      next: () => {
        this.toastr.success('Command retried successfully');
        this.onRetried.emit();
      },
      error: (err) => {
        this.toastr.error('Failed to retry command: ' + (err.error?.message || err.message));
      }
    });
  }

  deleteCommand(id: number, event: MouseEvent): void {
    event.stopPropagation();
    this.aiService.deleteAiCommand(id).subscribe({
      next: () => {
        this.toastr.success('Command deleted successfully');
        this.onDeleted.emit();
      },
      error: (err) => {
        this.toastr.error('Failed to delete command: ' + (err.error?.message || err.message));
      }
    });
  }

  deleteObsoleteMrRelated(): void {
    const obsoleteCommands = this.commands.filter(cmd =>
      cmd.gitlabMrWebUrl !== null &&
      cmd.gitlabMrGitlabStatus === null &&
      cmd.gitlabMrStrapiStatus === null
    );

    if (obsoleteCommands.length === 0) {
      this.toastr.info('No obsolete MR related commands found');
      return;
    }

    const deleteObservables = obsoleteCommands.map(cmd => this.aiService.deleteAiCommand(cmd.id));

    forkJoin(deleteObservables).subscribe({
      next: () => {
        this.toastr.success(`${obsoleteCommands.length} commands deleted successfully`);
        this.onDeleted.emit();
      },
      error: (err) => {
        this.toastr.error('Failed to delete some commands: ' + (err.error?.message || err.message));
        this.onDeleted.emit();
      }
    });
  }
}
